import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { updateProjectStatus } from "@/api/projects"
import { ProjectStatus, PROJECT_STATUS } from "@/interfaces/project_types"
import { toast } from "sonner"

// Helper function to get status background color
const getStatusBgColor = (status: string): string => {
    switch (status.toLowerCase()) {
        case "active":
            return "bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800"
        case "hold":
            return "bg-blue-100 dark:bg-blue-900 hover:bg-blue-200 dark:hover:bg-blue-800"
        case "review":
            return "bg-yellow-100 dark:bg-yellow-900 hover:bg-yellow-200 dark:hover:bg-yellow-800"
        case "completed":
            return "bg-green-100 dark:bg-green-900 hover:bg-green-200 dark:hover:bg-green-800"
        default:
            return "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
    }
}

/**
 * InteractableStatus component
 * @param status - The current status of the project
 * @param project_id - The ID of the project
 * @returns A button that allows the user to change the status of the project
 */
export function InteractableStatus({ status, project_id }: { status: string, project_id: string }) {
    const [currentStatus, setCurrentStatus] = useState(status)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        setCurrentStatus(status)
    }, [status])

    const handleStatusChange = async (newStatus: ProjectStatus) => {
        if (newStatus === currentStatus) {
            setIsDialogOpen(false)
            return
        }

        setIsLoading(true)
        setError(null)

        try {
            const updateResponse = await updateProjectStatus(project_id, newStatus)
            
            if (updateResponse === 200) {
                setCurrentStatus(newStatus)
                setIsDialogOpen(false)
                toast.success("Status updated successfully")
            } else {
                setError("Failed to update status")
                toast.error("Failed to update status")
            }
        } catch (err) {
            setError("An error occurred while updating the status")
            console.error("Error updating project status:", err)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
                <Button 
                    variant="ghost" 
                    className={`h-auto p-2 text-gray-800 dark:text-gray-200 font-medium ${getStatusBgColor(currentStatus)}`}
                >
                    {currentStatus}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Change Project Status</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-sm text-muted-foreground">
                        Current status: <span className="font-medium">{currentStatus}</span>
                    </div>
                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                            {error}
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                        {PROJECT_STATUS.map((statusOption) => (
                            <Button
                                key={statusOption}
                                variant={statusOption === currentStatus ? "default" : "outline"}
                                onClick={() => handleStatusChange(statusOption)}
                                disabled={isLoading}
                                className="w-full"
                            >
                                {isLoading ? "..." : statusOption}
                            </Button>
                        ))}
                    </div>
                </div>
                <DialogFooter>
                    <Button 
                        variant="outline" 
                        onClick={() => setIsDialogOpen(false)}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}